import type { ChartingLibraryFeatureset } from "~/chart";

// the tradingview charting library only accepts a subset of all Olsondb timezones that exist
interface StringMap {
  [key: string]: string;
}
export const UTC_TO_OLSON: StringMap = {
  "-11:00": "Pacific/Niue",
  "-10:00": "Pacific/Honolulu",
  "-09:00": "America/Juneau",
  "-08:00": "America/Los_Angeles",
  "-07:00": "America/Phoenix",
  "-06:00": "America/Mexico_City",
  "-05:00": "America/Bogota",
  "-04:00": "America/Caracas",
  "-03:00": "America/Argentina/Buenos_Aires",
  "-02:00": "Atlantic/South_Georgia",
  "-01:00": "Atlantic/Cape_Verde",
  "+00:00": "Atlantic/Reykjavik",
  "+01:00": "Europe/Berlin",
  "+02:00": "Europe/Athens",
  "+03:00": "Asia/Qatar",
  "+04:00": "Asia/Dubai",
  "+05:00": "Asia/Ashkhabad",
  "+06:00": "Asia/Almaty",
  "+07:00": "Asia/Bangkok",
  "+08:00": "Asia/Hong_Kong",
  "+09:00": "Asia/Tokyo",
  "+10:00": "Australia/Brisbane",
  "+11:00": "Pacific/Norfolk",
  "+12:00": "Pacific/Fiji",
};

// TradingView Features - extracted from ChartingLibraryFeatureset
export interface TradingViewFeature {
  name: ChartingLibraryFeatureset;
  description: string;
  defaultValue: boolean;
}

export const TRADINGVIEW_FEATURES: TradingViewFeature[] = [
  {
    name: "use_localstorage_for_settings",
    description:
      "Allows storing all properties (including favorites) to the localstorage",
    defaultValue: true,
  },
  {
    name: "items_favoriting",
    description:
      "Disabling this feature hides 'Favorite this item' icon for Drawings and Intervals",
    defaultValue: true,
  },
  {
    name: "save_chart_properties_to_local_storage",
    description:
      "Can be disabled to forbid storing chart properties to the localstorage while allowing to save other properties",
    defaultValue: true,
  },
  {
    name: "create_volume_indicator_by_default",
    description: "Add the volume indicator upon initialisation of the chart",
    defaultValue: true,
  },
  {
    name: "create_volume_indicator_by_default_once",
    description:
      "Prevent the volume indicator from being recreated when an instrument or a resolution is switched",
    defaultValue: true,
  },
  {
    name: "volume_force_overlay",
    description:
      "Places Volume indicator on the same pane with the main series",
    defaultValue: true,
  },
  {
    name: "right_bar_stays_on_scroll",
    description:
      "Determines the behavior of Zoom feature: bar under the mouse cursor stays in the same place if this feature is disabled",
    defaultValue: true,
  },
  {
    name: "constraint_dialogs_movement",
    description: "Keeps the dialogs within the chart",
    defaultValue: true,
  },
  {
    name: "charting_library_debug_mode",
    description: "Enables logs",
    defaultValue: false,
  },
  {
    name: "side_toolbar_in_fullscreen_mode",
    description: "This enables Drawings Toolbar in the fullscreen mode",
    defaultValue: false,
  },
  {
    name: "header_in_fullscreen_mode",
    description: "Enables header widget DOM element in the fullscreen mode",
    defaultValue: false,
  },
  {
    name: "disable_resolution_rebuild",
    description:
      "Shows bar time exactly as provided by the data feed with no adjustments",
    defaultValue: false,
  },
  {
    name: "chart_scroll",
    description: "Allows chart scrolling",
    defaultValue: true,
  },
  {
    name: "chart_zoom",
    description: "Allows chart zooming",
    defaultValue: true,
  },
  {
    name: "horz_touch_drag_scroll",
    description: "Chart handles horizontal pointer movements on touch screens",
    defaultValue: true,
  },
  {
    name: "vert_touch_drag_scroll",
    description: "Chart handles vertical pointer movements on touch screens",
    defaultValue: true,
  },
  {
    name: "mouse_wheel_scroll",
    description: "Chart scrolling with horizontal mouse wheel is enabled",
    defaultValue: true,
  },
  {
    name: "pressed_mouse_move_scroll",
    description: "Chart scrolling with left mouse button pressed is allowed",
    defaultValue: true,
  },
  {
    name: "mouse_wheel_scale",
    description: "Series scaling with a mouse wheel is enabled",
    defaultValue: true,
  },
  {
    name: "pinch_scale",
    description:
      "Series scaling with pinch/zoom gestures (this option is supported on touch devices) is enabled",
    defaultValue: true,
  },
  {
    name: "axis_pressed_mouse_move_scale",
    description: "Axis scaling with left mouse button pressed is allowed",
    defaultValue: true,
  },
  {
    name: "low_density_bars",
    description: "Allows zooming in to show up to one bar in the viewport",
    defaultValue: false,
  },
  {
    name: "uppercase_instrument_names",
    description:
      "Disabling this feature allows a user to enter case-sensitive symbols",
    defaultValue: true,
  },
  {
    name: "no_min_chart_width",
    description: "Disables minimum chart width limitation",
    defaultValue: false,
  },
  {
    name: "fix_left_edge",
    description: "Prevents scrolling to the left of the first historical bar",
    defaultValue: false,
  },
  {
    name: "lock_visible_time_range_on_resize",
    description: "Prevents changing visible time area on chart resizing",
    defaultValue: false,
  },
  {
    name: "shift_visible_range_on_new_bar",
    description: "Chart is scrolled one point to the left when a new bar comes",
    defaultValue: true,
  },
  {
    name: "custom_resolutions",
    description: "Enables the possibility to add custom resolutions",
    defaultValue: false,
  },
  {
    name: "end_of_period_timescale_marks",
    description: "Toggles the timeline marks to display the bar's end time",
    defaultValue: false,
  },
  {
    name: "cropped_tick_marks",
    description:
      "If disabled, partially visible price labels on price axis will be hidden",
    defaultValue: true,
  },
  {
    name: "study_overlay_compare_legend_option",
    description:
      "Applies symbol display mode (ticker/description) to overlay/compare studies in the status line",
    defaultValue: false,
  },
  {
    name: "study_symbol_ticker_description",
    description:
      "Applies symbol display mode (ticker/description) to indicator inputs in the status line",
    defaultValue: false,
  },
  {
    name: "auto_enable_symbol_labels",
    description: "Displays Symbol Name Label when comparing Symbols",
    defaultValue: true,
  },
  {
    name: "insert_indicator_dialog_shortcut",
    description: "Enables insert indicator dialog shortcut (/)",
    defaultValue: true,
  },
  {
    name: "two_character_bar_marks_labels",
    description: "Display at most two characters in bar marks",
    defaultValue: false,
  },
  {
    name: "confirm_overwrite_if_chart_layout_with_name_exists",
    description:
      "Prompt to confirm overwriting chart layouts with the same name when saving, renaming, or cloning",
    defaultValue: false,
  },
  {
    name: "determine_first_data_request_size_using_visible_range",
    description:
      "Calculate the request size based on the amount of bars which will be visible on the chart",
    defaultValue: false,
  },
  {
    name: "move_logo_to_main_pane",
    description:
      "Places the logo on the main series pane instead of the bottom pane",
    defaultValue: false,
  },
  {
    name: "study_templates",
    description:
      "Enables a button in the header to load/save Indicator template",
    defaultValue: false,
  },
  {
    name: "datasource_copypaste",
    description: "Enables copying of drawings and studies",
    defaultValue: true,
  },
  {
    name: "seconds_resolution",
    description: "Enables the support of resolutions that start from 1 second",
    defaultValue: false,
  },
  {
    name: "tick_resolution",
    description: "Enables the support of tick resolution",
    defaultValue: false,
  },
  {
    name: "secondary_series_extend_time_scale",
    description:
      "Enables a feature to allow an additional series to extend the time scale",
    defaultValue: false,
  },
  {
    name: "header_widget",
    description: "Removes the header from the chart",
    defaultValue: true,
  },
  {
    name: "header_symbol_search",
    description: "Hides the symbol search button from the header",
    defaultValue: true,
  },
  {
    name: "symbol_search_hot_key",
    description: "Symbol search by pressing any key",
    defaultValue: true,
  },
  {
    name: "allow_arbitrary_symbol_search_input",
    description:
      "Enables or disables the ability for users to enter arbitrary input in the Symbol Search dialog",
    defaultValue: false,
  },
  {
    name: "header_resolutions",
    description: "Hides the resolution button from the header",
    defaultValue: true,
  },
  {
    name: "show_interval_dialog_on_key_press",
    description:
      "Displays a 'Change interval' dialog to specify another resolution",
    defaultValue: true,
  },
  {
    name: "header_chart_type",
    description: "Hides the chart type button from the header",
    defaultValue: true,
  },
  {
    name: "header_settings",
    description: "Relates to Chart Properties button",
    defaultValue: true,
  },
  {
    name: "header_indicators",
    description: "Hides the indicators button from the header",
    defaultValue: true,
  },
  {
    name: "header_compare",
    description: "Hides the compare button from the header",
    defaultValue: true,
  },
  {
    name: "header_undo_redo",
    description: "Hides the undo/redo button from the header",
    defaultValue: true,
  },
  {
    name: "header_quick_search",
    description: "Hides the quick search button from the header",
    defaultValue: true,
  },
  {
    name: "header_screenshot",
    description: "Hides the screenshot button from the header",
    defaultValue: true,
  },
  {
    name: "header_fullscreen_button",
    description: "Hides the fullscreen button from the header",
    defaultValue: true,
  },
  {
    name: "border_around_the_chart",
    description: "Adds a 2px padding to the chart",
    defaultValue: true,
  },
  {
    name: "header_saveload",
    description:
      "Hides save/load buttons (the feature is not part of header_widget featureset)",
    defaultValue: true,
  },
  {
    name: "left_toolbar",
    description: "Hides the left toolbar aka drawings toolbar",
    defaultValue: true,
  },
  {
    name: "control_bar",
    description: "Relates to the navigation buttons at the bottom of the chart",
    defaultValue: true,
  },
  {
    name: "timeframes_toolbar",
    description: "Hide the timeframe toolbar at the bottom of the chart",
    defaultValue: true,
  },
  {
    name: "legend_widget",
    description: "Disabling this feature hides the legend widget",
    defaultValue: true,
  },
  {
    name: "display_legend_on_all_charts",
    description:
      "Display legend on all diagrams regardless of crosshair synchronization",
    defaultValue: false,
  },
  {
    name: "object_tree_legend_mode",
    description: "Display object tree button in the legend at a small width",
    defaultValue: true,
  },
  {
    name: "edit_buttons_in_legend",
    description: "Removes all buttons from the legend except the ellipsis menu",
    defaultValue: true,
  },
  {
    name: "show_hide_button_in_legend",
    description: "Removes the eye button that hides/shows a chart",
    defaultValue: true,
  },
  {
    name: "format_button_in_legend",
    description:
      "Removes the cog icon for accessing chart/indicator's settings",
    defaultValue: true,
  },
  {
    name: "delete_button_in_legend",
    description: "Removes the delete button",
    defaultValue: true,
  },
  {
    name: "context_menus",
    description:
      "Doesn't display any context menu when right clicking anywhere in the UI",
    defaultValue: true,
  },
  {
    name: "pane_context_menu",
    description:
      "Doesn't display the context menu when right clicking on the chart",
    defaultValue: true,
  },
  {
    name: "scales_context_menu",
    description:
      "Doesn't display the context menu when right clicking on either price scale or timescale",
    defaultValue: true,
  },
  {
    name: "legend_context_menu",
    description:
      "Doesn't display the context menu when right clicking on the legend",
    defaultValue: true,
  },
  {
    name: "main_series_scale_menu",
    description:
      "Displays the settings button in the bottom right corner of the chart",
    defaultValue: true,
  },
  {
    name: "display_market_status",
    description: "Hides the market status from the legend",
    defaultValue: true,
  },
  {
    name: "remove_library_container_border",
    description: "Sets the border style to 0px & padding to 1px",
    defaultValue: true,
  },
  {
    name: "property_pages",
    description: "Disables all property pages",
    defaultValue: true,
  },
  {
    name: "show_chart_property_page",
    description: "Turning this feature off disables Properties",
    defaultValue: true,
  },
  {
    name: "chart_property_page_scales",
    description: "Allows overrides for the price scale",
    defaultValue: true,
  },
  {
    name: "chart_property_page_trading",
    description: "This feature is for the Trading Platform only",
    defaultValue: true,
  },
  {
    name: "chart_property_page_right_margin_editor",
    description: "Shows the right margin editor in the setting dialog",
    defaultValue: true,
  },
  {
    name: "countdown",
    description: "Displays a countdown label on a price scale",
    defaultValue: true,
  },
  {
    name: "dont_show_boolean_study_arguments",
    description: "Hides true/false study arguments",
    defaultValue: false,
  },
  {
    name: "hide_last_na_study_output",
    description: "Hides last n/a study output data",
    defaultValue: false,
  },
  {
    name: "symbol_info",
    description: "Enables the symbol info dialog",
    defaultValue: true,
  },
  {
    name: "timezone_menu",
    description: "Disables timezone context menu",
    defaultValue: true,
  },
  {
    name: "snapshot_trading_drawings",
    description: "Includes orders/positions/executions in the screenshot",
    defaultValue: false,
  },
  {
    name: "source_selection_markers",
    description: "Disables selection markers for series and indicators",
    defaultValue: true,
  },
  {
    name: "go_to_date",
    description: "Allows you to jump to a particular bar using 'Go to' dialog",
    defaultValue: true,
  },
  {
    name: "adaptive_logo",
    description:
      "Allows you to hide 'charts by TradingView' text on small-screen devices",
    defaultValue: true,
  },
  {
    name: "show_dom_first_time",
    description:
      "Shows DOM panel when a user opens the Chart for the first time",
    defaultValue: false,
  },
  {
    name: "hide_left_toolbar_by_default",
    description:
      "Hides left toolbar when a user opens the Chart for the first time",
    defaultValue: false,
  },
  {
    name: "chart_style_hilo",
    description: "Adds High-low option to chart style controls",
    defaultValue: true,
  },
  {
    name: "chart_style_hilo_last_price",
    description:
      "Enables last price line and price axis label on High-low chart style",
    defaultValue: false,
  },
  {
    name: "pricescale_currency",
    description:
      "Displays the currency in which the instrument is traded on the price axes",
    defaultValue: false,
  },
  {
    name: "pricescale_unit",
    description:
      "Displays the unit in which the instrument is traded on the price axes",
    defaultValue: false,
  },
  {
    name: "scales_date_format",
    description: "Displays Date Format selector in Chart Settings",
    defaultValue: true,
  },
  {
    name: "popup_hints",
    description:
      "Displays popup hints about possible mouse/shortcuts/UI actions",
    defaultValue: true,
  },
  {
    name: "save_shortcut",
    description: "Enables the save shortcut",
    defaultValue: true,
  },
  {
    name: "show_right_widgets_panel_by_default",
    description: "Opens right widget toolbar on first launch",
    defaultValue: true,
  },
  {
    name: "show_object_tree",
    description:
      "Shows the object tree button in the left or right panel depending on the product and configuration",
    defaultValue: true,
  },
  {
    name: "show_spread_operators",
    description: "Shows the spread operators in the Symbol Search dialog",
    defaultValue: false,
  },
  {
    name: "hide_exponentiation_spread_operator",
    description:
      "Hide exponentiation spread operator (^) in the Symbol Search dialog",
    defaultValue: false,
  },
  {
    name: "hide_reciprocal_spread_operator",
    description:
      "Hide reciprocal spread operator (1/x) in the Symbol Search dialog",
    defaultValue: false,
  },
  {
    name: "compare_symbol_search_spread_operators",
    description: "Shows the spread operators in the Compare Search dialog",
    defaultValue: false,
  },
  {
    name: "studies_symbol_search_spread_operators",
    description: "Shows the spread operators for Studies",
    defaultValue: false,
  },
  {
    name: "hide_resolution_in_legend",
    description:
      "Hide the interval (D, 2D, W, M, etc.) in the chart legend and the data window",
    defaultValue: false,
  },
  {
    name: "hide_unresolved_symbols_in_legend",
    description:
      "Hide unresolved symbols in the chart legend and the data window",
    defaultValue: false,
  },
  {
    name: "show_zoom_and_move_buttons_on_touch",
    description:
      "On touch device show the zoom and move buttons at the bottom of the chart",
    defaultValue: false,
  },
  {
    name: "hide_main_series_symbol_from_indicator_legend",
    description:
      "Hide the optional symbol input value from the indicator's legend if 'Main chart symbol' option is selected",
    defaultValue: true,
  },
  {
    name: "hide_price_scale_global_last_bar_value",
    description:
      "Hide the global last price label on price scale if last bar is outside of visible range",
    defaultValue: false,
  },
  {
    name: "show_average_close_price_line_and_label",
    description:
      "Hide the visibility settings of the label and the average close price line",
    defaultValue: false,
  },
  {
    name: "hide_image_invalid_symbol",
    description: "Hide image shown to illustrate symbol is invalid",
    defaultValue: false,
  },
  {
    name: "hide_object_tree_and_price_scale_exchange_label",
    description: "Show/Hide the exchange label from the displayed label",
    defaultValue: false,
  },
  {
    name: "scales_time_hours_format",
    description: "Displays Time Format selector in Chart Settings",
    defaultValue: true,
  },
  {
    name: "use_na_string_for_not_available_values",
    description: "Show a literal 'n/a' for not available values instead of '∅'",
    defaultValue: false,
  },
  {
    name: "pre_post_market_sessions",
    description: "Enable pre and post market session support",
    defaultValue: false,
  },
  {
    name: "show_percent_option_for_right_margin",
    description:
      "Show the option to specify the default right margin in percentage within chart settings dialog",
    defaultValue: false,
  },
  {
    name: "lock_visible_time_range_when_adjusting_percentage_right_margin",
    description:
      "Lock the visible range when adjusting the percentage right margin via the settings dialog",
    defaultValue: false,
  },
  {
    name: "iframe_loading_compatibility_mode",
    description:
      "Alternative loading mode for the library, which can be used to support older browsers",
    defaultValue: false,
  },
  {
    name: "use_last_visible_bar_value_in_legend",
    description: "Use the last (rightmost) visible bar value in the legend",
    defaultValue: false,
  },
  {
    name: "symbol_info_long_description",
    description:
      "Enable long symbol descriptions to be shown in the main series and compare studies legends",
    defaultValue: false,
  },
  {
    name: "symbol_info_price_source",
    description:
      "Enable symbol price source to be shown in the main series and compare studies legends",
    defaultValue: false,
  },
  {
    name: "chart_template_storage",
    description: "Enable saving/loading of chart templates",
    defaultValue: false,
  },
  {
    name: "request_only_visible_range_on_reset",
    description:
      "When chart data is reset, then re-request data for just the visible range",
    defaultValue: false,
  },
  {
    name: "clear_price_scale_on_error_or_empty_bars",
    description:
      "Clear pane price scales when the main series has an error or has no bars",
    defaultValue: true,
  },
  {
    name: "legend_inplace_edit",
    description:
      "Enables the ability to in-place edit of studies or series in legend",
    defaultValue: true,
  },
  {
    name: "disable_legend_inplace_resolution_change",
    description:
      "Disables the ability to in-place change the resolution of a series in the legend",
    defaultValue: false,
  },
  {
    name: "disable_legend_inplace_symbol_change",
    description:
      "Disables the ability to in-place change the symbol of a series or study in the legend",
    defaultValue: false,
  },
  {
    name: "show_symbol_logos",
    description:
      "Display logos for the symbols within the symbol search dialog, and the watchlist widget",
    defaultValue: false,
  },
  {
    name: "show_exchange_logos",
    description:
      "Display logos for the exchanges within the symbol search dialog",
    defaultValue: false,
  },
  {
    name: "show_symbol_logo_in_legend",
    description: "Display the main symbol's logo within the legend",
    defaultValue: true,
  },
  {
    name: "show_symbol_logo_for_compare_studies",
    description:
      "Display the symbol's logo within the legend for compare studies",
    defaultValue: true,
  },
  {
    name: "always_show_legend_values_on_mobile",
    description: "Display legend values when on mobile",
    defaultValue: false,
  },
  {
    name: "studies_extend_time_scale",
    description:
      "Enable studies to extend the time scale, if enabled in the study metainfo",
    defaultValue: false,
  },
  {
    name: "accessible_keyboard_shortcuts",
    description:
      "Replace Alt + Z with Tab, Tab with Shift + Arrow right, Shift + Tab with Shift + Arrow left",
    defaultValue: false,
  },
  {
    name: "aria_detailed_chart_descriptions",
    description:
      "Generates a more detailed ARIA description of the chart for screen readers",
    defaultValue: false,
  },
  {
    name: "aria_crosshair_price_description",
    description:
      "Announces, via the screen reader, the price when the crosshair is moved on the chart",
    defaultValue: false,
  },
  {
    name: "saveload_separate_drawings_storage",
    description:
      "Enables an alternative saving and loading mode for the library",
    defaultValue: false,
  },
  {
    name: "disable_pulse_animation",
    description: "Disables the pulse animation when chart type is set to Line",
    defaultValue: false,
  },
  {
    name: "iframe_loading_same_origin",
    description:
      "Alternative loading mode when the iframe content must be served from the same origin",
    defaultValue: false,
  },
  {
    name: "library_custom_color_themes",
    description: "Enables custom color themes features",
    defaultValue: true,
  },
  {
    name: "use_symbol_name_for_header_toolbar",
    description:
      "Uses the name of a symbol rather than its ticker name in the Symbol Search dialog and header button",
    defaultValue: false,
  },
  {
    name: "inactivity_gaps",
    description: "Enables the ability to display inactivity gaps on charts",
    defaultValue: false,
  },
  {
    name: "long_press_floating_tooltip",
    description: "Enables the floating tooltip on long press",
    defaultValue: true,
  },
];
